import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Phone, Mail, User, PoundSterling, Calendar, Clock, ExternalLink, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  if (v == null || isNaN(v)) return '£0';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const formatWeekLabel = (start, end) => {
  if (!start || !end) return '';
  const opts = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const yearOpts = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
  return `${new Date(start).toLocaleDateString('en-GB', opts)} – ${new Date(end).toLocaleDateString('en-GB', yearOpts)}`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

function DealRow({ deal }) {
  const days = daysUntil(deal.expiresAt);
  const isUrgent = days !== null && days <= 3;

  return (
    <div className={`py-3 px-4 border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors ${isUrgent ? 'bg-amber-50/40' : ''}`} data-testid="chase-deal-row">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-3.5 h-3.5 text-stone-400 shrink-0" />
            <span className="text-sm font-semibold text-stone-800">{deal.name}</span>
            {days !== null && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                isUrgent ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-500'
              }`}>
                <Clock className="w-3 h-3 inline mr-0.5" />{days}d left
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-xs text-stone-500 mb-1">
            {deal.email && (
              <a href={`mailto:${deal.email}`} className="flex items-center gap-1 hover:text-stone-700">
                <Mail className="w-3 h-3" /> {deal.email}
              </a>
            )}
            {deal.phone && (
              <a href={`tel:${deal.phone}`} className="flex items-center gap-1 hover:text-stone-700">
                <Phone className="w-3 h-3" /> {deal.phone}
              </a>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-stone-400">
            {deal.weddingDate && (
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(deal.weddingDate)}</span>
            )}
            {deal.venue && <span>{deal.venue}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 text-sm font-bold text-stone-800">
            <PoundSterling className="w-3.5 h-3.5 text-stone-400" />{formatCurrency(deal.amount)}
          </div>
          {deal.packageName && <p className="text-[11px] text-stone-400 mt-0.5 max-w-[160px] truncate">{deal.packageName}</p>}
          {deal.quoteUrl && (
            <a href={deal.quoteUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5 justify-end mt-1">
              View Quote <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function WeekSection({ title, dateLabel, deals }) {
  const totalValue = deals.reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <Card className="bg-white border-stone-100 shadow-sm overflow-hidden">
      <CardHeader className="pb-3 border-b border-stone-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium text-stone-800">{title}</CardTitle>
            <p className="text-xs text-stone-500 mt-0.5">{dateLabel}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-stone-800">{deals.length}</p>
            <p className="text-xs text-stone-500">{formatCurrency(totalValue)}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {deals.length === 0 ? (
          <p className="text-sm text-stone-400 p-5 text-center">No quotes sent this week</p>
        ) : (
          deals.map((deal, i) => <DealRow key={deal.quoteUrl || `${deal.name}-${i}`} deal={deal} />)
        )}
      </CardContent>
    </Card>
  );
}

export default function ChaseTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chase/deals`);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Failed to fetch chase deals:', e); // eslint-disable-line no-console
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const weeks = data?.weeks || [];
  const allDeals = weeks.flatMap(w => w.deals || []);
  const totalValue = allDeals.reduce((sum, d) => sum + (d.amount || 0), 0);
  const urgentCount = allDeals.filter(d => d.expiresAt && daysUntil(d.expiresAt) <= 3).length;

  const weekLabels = ['This Week', 'Last Week', '2 Weeks Ago', '3 Weeks Ago'];

  return (
    <div className="space-y-6" data-testid="chase-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Chase List</h2>
          <p className="text-sm text-stone-500">Active quotes that need following up</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => window.open(`${API}/api/brevo/win-back-csv`, '_blank')} variant="outline" className="border-stone-200" data-testid="download-winback">
            <Download className="w-4 h-4 mr-2" /> Win-Back CSV
          </Button>
          <Button onClick={fetchData} disabled={loading} variant="outline" className="border-stone-200" data-testid="refresh-chase">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Headline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Active Quotes</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{allDeals.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Total Value</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalValue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Expiring ≤3 Days</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{urgentCount}</p>
          </CardContent>
        </Card>
        {weeks.length > 0 && (
          <Card className="bg-white border-stone-100 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Sent This Week</p>
              <p className="text-2xl font-bold text-stone-900 mt-1">{(weeks[0]?.deals || []).length}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {weeks.map((w, i) => (
        <WeekSection
          key={w.start || i}
          title={weekLabels[i] || `${i} Weeks Ago`}
          dateLabel={formatWeekLabel(w.start, w.end)}
          deals={w.deals || []}
        />
      ))}
    </div>
  );
}
